import React, { useEffect, useState } from 'react';
import {
  Container,
  Box,
  Typography,
  Button,
  Grid,
  CircularProgress,
  Alert,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { fetchGymSlots, deleteSlot } from '../../redux/slotSlice';
import { fetchPartnerGyms } from '../../redux/gymSlice';
import { RootState, AppDispatch } from '../../redux/store';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PeopleIcon from '@mui/icons-material/People';
import EventIcon from '@mui/icons-material/Event';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';

export const SlotsListPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { slots, isLoading, error, totalCount } = useSelector((state: RootState) => state.slot);
  const { gyms } = useSelector((state: RootState) => state.gym);
  
  const [filters, setFilters] = useState({
    gymId: '',
    date: '',
    date_from: '',
    date_to: '',
    is_active: 'all'
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [slotToDelete, setSlotToDelete] = useState<{ gymId: number; slotId: number } | null>(null);

  useEffect(() => {
    dispatch(fetchPartnerGyms());
  }, [dispatch]);

  useEffect(() => {
    if (filters.gymId) {
      const filterParams: any = {};
      if (filters.date) filterParams.date = filters.date;
      if (filters.date_from) filterParams.date_from = filters.date_from;
      if (filters.date_to) filterParams.date_to = filters.date_to;
      if (filters.is_active !== 'all') filterParams.is_active = filters.is_active === 'true';

      dispatch(fetchGymSlots({ gymId: parseInt(filters.gymId), filters: filterParams }));
    }
  }, [dispatch, filters]);

  const handleFilterChange = (field: string, value: string) => {
    setFilters({ ...filters, [field]: value });
  };

  const handleDeleteClick = (gymId: number, slotId: number) => {
    setSlotToDelete({ gymId, slotId });
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (slotToDelete) {
      await dispatch(deleteSlot({ gymId: slotToDelete.gymId, slotId: slotToDelete.slotId }));
      setDeleteDialogOpen(false);
      setSlotToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSlotToDelete(null);
  };

  const formatTime = (time: string) => {
    return time.substring(0, 5);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-IN', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  if (isLoading && slots.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress sx={{ color: '#14B8A6' }} />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 4 }, px: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'start', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
            Slot Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage time slots for your gyms
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/slots/create')}
          disabled={!filters.gymId}
          sx={{
            height: '48px',
            minWidth: { xs: '100%', sm: '160px' },
            backgroundColor: '#14B8A6',
            '&:hover': {
              backgroundColor: '#0F9688'
            }
          }}
        >
          Create Slots
        </Button>
      </Box>

      {/* Filters */}
      <Box sx={{ mb: 4, p: 3, border: '1px solid #e0e0e0', borderRadius: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
          Filters
        </Typography>
        <Grid container spacing={{ xs: 3, sm: 3, md: 3.5 }} sx={{ '& .MuiGrid-item': { paddingTop: '24px !important' } }}>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Select Gym</InputLabel>
              <Select
                value={filters.gymId}
                onChange={(e) => handleFilterChange('gymId', e.target.value)}
                label="Select Gym"
              >
                <MenuItem value="">All Gyms</MenuItem>
                {gyms.map((gym) => (
                  <MenuItem key={gym.id} value={gym.id.toString()}>
                    {gym.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Specific Date"
              type="date"
              value={filters.date}
              onChange={(e) => handleFilterChange('date', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              label="From Date"
              type="date"
              value={filters.date_from}
              onChange={(e) => handleFilterChange('date_from', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              label="To Date"
              type="date"
              value={filters.date_to}
              onChange={(e) => handleFilterChange('date_to', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.is_active}
                onChange={(e) => handleFilterChange('is_active', e.target.value)}
                label="Status"
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="true">Active</MenuItem>
                <MenuItem value="false">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Stats */}
      {filters.gymId && (
        <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#f9fafb' }}>
          <Typography variant="body2" sx={{ color: '#666' }}>
            <strong>Total Slots:</strong> {totalCount}
          </Typography>
        </Box>
      )}

      {/* Empty State */}
      {!filters.gymId ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <EventIcon sx={{ fontSize: '4rem', color: '#ccc', mb: 2 }} />
          <Typography variant="h5" sx={{ color: '#666', mb: 2, fontWeight: 600 }}>
            Select a Gym
          </Typography>
          <Typography variant="body1" sx={{ color: '#999', mb: 4 }}>
            Please select a gym from the filters to view and manage slots
          </Typography>
        </Box>
      ) : slots.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <EventIcon sx={{ fontSize: '4rem', color: '#ccc', mb: 2 }} />
          <Typography variant="h5" sx={{ color: '#666', mb: 2, fontWeight: 600 }}>
            No Slots Found
          </Typography>
          <Typography variant="body1" sx={{ color: '#999', mb: 4 }}>
            Create your first slot to start managing bookings
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/slots/create')}
            sx={{
              height: '48px',
              px: 4,
              backgroundColor: '#14B8A6',
              '&:hover': {
                backgroundColor: '#0F9688'
              }
            }}
          >
            Create First Slot
          </Button>
        </Box>
      ) : (
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          {slots.map((slot) => (
            <Grid item xs={12} sm={6} md={4} key={slot.id}>
              <Box
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  p: 2.5,
                  transition: 'border-color 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    borderColor: '#14B8A6',
                    boxShadow: '0 2px 8px rgba(20, 184, 166, 0.1)'
                  }
                }}
              >
                <Box sx={{ flexGrow: 1 }}>
                  {/* Date */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <EventIcon sx={{ fontSize: '1.25rem', color: '#14B8A6', mr: 1 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
                      {formatDate(slot.date)}
                    </Typography>
                  </Box>

                  {/* Time */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <AccessTimeIcon sx={{ fontSize: '1rem', color: '#666', mr: 1 }} />
                    <Typography variant="body2" sx={{ color: '#666' }}>
                      {formatTime(slot.start_time)} - {formatTime(slot.end_time)}
                    </Typography>
                  </Box>

                  {/* Capacity */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <PeopleIcon sx={{ fontSize: '1rem', color: '#666', mr: 1 }} />
                    <Typography variant="body2" sx={{ color: '#666' }}>
                      {slot.booked_count}/{slot.capacity} booked
                    </Typography>
                    <Chip
                      label={`${slot.occupancy_percentage || 0}%`}
                      size="small"
                      sx={{
                        ml: 1,
                        height: '20px',
                        fontSize: '0.7rem',
                        backgroundColor: slot.occupancy_percentage && slot.occupancy_percentage > 80 ? '#fee' : '#e8f5e9',
                        color: slot.occupancy_percentage && slot.occupancy_percentage > 80 ? '#c62828' : '#2e7d32'
                      }}
                    />
                  </Box>

                  {/* Price */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <AttachMoneyIcon sx={{ fontSize: '1rem', color: '#666', mr: 1 }} />
                    <Typography variant="body2" sx={{ color: '#666' }}>
                      ₹{slot.price_cash} / {slot.price_coins} coins
                    </Typography>
                  </Box>

                  {/* Status */}
                  <Box>
                    <Chip
                      label={slot.is_active ? 'Active' : 'Inactive'}
                      color={slot.is_active ? 'success' : 'default'}
                      size="small"
                      sx={{ fontWeight: 500, height: '24px' }}
                    />
                  </Box>
                </Box>

                {/* Actions */}
                <Box sx={{ display: 'flex', gap: 1, mt: 2, pt: 2, borderTop: '1px solid #f0f0f0' }}>
                  <Button
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => navigate(`/slots/${slot.id}/edit?gymId=${filters.gymId}`)}
                    sx={{ flex: 1, fontSize: '0.8125rem' }}
                  >
                    Edit
                  </Button>
                  <Button
                    size="small"
                    startIcon={<DeleteIcon />}
                    color="error"
                    onClick={() => handleDeleteClick(parseInt(filters.gymId), slot.id)}
                    sx={{ flex: 1, fontSize: '0.8125rem' }}
                  >
                    Delete
                  </Button>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Slot</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this slot? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SlotsListPage;
